/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Modal,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Button,
    List,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { m } from 'framer-motion';
import { useSelector, dispatch } from '../../redux/store';
import { getPracticeId } from '../../redux/slices/clincheck';
import { getSearchWpTemplateList } from '../../redux/slices/whatsAppMessages';
import { updateError } from '../../redux/slices/user';
import { getWhatsappAccount } from '../../redux/slices/whatsappAccount';
import { callApi } from '../../utils/apiCall';
import { APPLE, appleInputSx } from '../templates/appleDesignTokens';
import RequestTemplateDialog from '../templates/RequestTemplateDialog';
import WhatsAppTemplatePreviewPanel from './WhatsAppTemplatePreviewPanel';
import WhatsAppTemplateListItem from './WhatsAppTemplateListItem';
import {
    WA_GREEN,
    WA_LIGHT_GREEN,
    getUsageTypeColor,
    formatUsageType,
    matchesSearch,
} from './whatsAppTemplateUtils';

const WhatsAppTemplateSelector = ({
    open,
    onClose,
    templates = [],
    onSend,
    onSelect,
    selectedTemplate: externalSelectedTemplate,
    loading = false,
    contextMapping = {},
    title = 'Select WhatsApp Template',
    onRequestTemplate,
    onEditTemplate,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:768px)');
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const [internalSelectedTemplate, setInternalSelectedTemplate] = useState(null);
    const [mobileShowList, setMobileShowList] = useState(false);
    const prevOpenRef = useRef(false);

    // ── Internal create/edit dialog state ──
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Redux — pull wpAccountId + practiceId for built-in create/edit
    const { whatsappAccountData } = useSelector((state) => state.whatsappAccount);
    const wpAccountId = whatsappAccountData?.results?.[0]?.id;
    const practiceId = getPracticeId();

    const selectedTemplate = externalSelectedTemplate !== undefined ? externalSelectedTemplate : internalSelectedTemplate;

    // Compute existing template names for duplicate-name validation in create dialog
    const existingNames = useMemo(() => (templates || []).map(t => t.name), [templates]);

    // Ensure whatsapp account data is loaded (needed for create/edit API calls)
    useEffect(() => {
        if (open && !wpAccountId && practiceId) {
            dispatch(getWhatsappAccount(practiceId));
        }
    }, [open, wpAccountId, practiceId]);

    // Refresh template list when the selector opens
    useEffect(() => {
        if (open && wpAccountId) {
            dispatch(getSearchWpTemplateList({ wpAccountId }));
        }
    }, [open, wpAccountId]);

    useEffect(() => {
        if (open && !prevOpenRef.current) {
            setSearchText('');
            setSelectedCategory('ALL');
            if (templates.length > 0 && !externalSelectedTemplate) {
                setInternalSelectedTemplate(templates[0]);
            }
        }
        prevOpenRef.current = open;
    }, [open, templates, externalSelectedTemplate]);

    const usageTypes = useMemo(() => {
        const types = new Set(templates.map(t => t.usage_type).filter(Boolean));
        return ['ALL', ...Array.from(types)];
    }, [templates]);

    const usageTypeCounts = useMemo(() => {
        const counts = { ALL: templates.length };
        templates.forEach(t => {
            const type = t.usage_type || 'GENERAL';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        let filtered = templates;
        if (searchText) {
            filtered = filtered.filter(t =>
                matchesSearch(t.name, searchText) ||
                t.components?.some(c => c.text?.toLowerCase().includes(searchText.toLowerCase().trim()))
            );
        }
        if (selectedCategory !== 'ALL') {
            filtered = filtered.filter(t => t.usage_type === selectedCategory);
        }
        return filtered;
    }, [templates, searchText, selectedCategory]);

    const handleTemplateSelect = useCallback((template) => {
        setInternalSelectedTemplate(template);
        setMobileShowList(false);
        onSelect?.(template);
    }, [onSelect]);

    const handleSend = useCallback(() => {
        if (selectedTemplate) {
            onSend?.(selectedTemplate);
        }
    }, [selectedTemplate, onSend]);

    // ── Built-in create handler ──
    const handleCreateTemplate = useCallback(async (payload) => {
        if (!wpAccountId) return;
        setSubmitting(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/create_from_meta/`,
                'POST',
                payload
            );
            if (res.ok) {
                const tplName = res.data?.template_name || '';
                const category = res.data?.category || '';
                dispatch(updateError({
                    message: `Template "${tplName}" created as ${category} — pending Meta approval.`,
                    success: true,
                }));
                setCreateDialogOpen(false);
                dispatch(getSearchWpTemplateList({ wpAccountId }));
            } else {
                const errMsg = res.data?.detail || res.data?.error || 'Failed to create template';
                dispatch(updateError({ message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg), success: false }));
            }
        } catch {
            dispatch(updateError({ message: 'Failed to create template', success: false }));
        } finally {
            setSubmitting(false);
        }
    }, [wpAccountId, practiceId]);

    // ── Built-in edit handler ──
    const handleEditTemplate = useCallback(async (payload) => {
        if (!wpAccountId || !payload.templateId) return;
        setSubmitting(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${wpAccountId}/whatsapp-message-templates/${payload.templateId}/update_from_meta/`,
                'POST',
                { template: payload.template, components: payload.components }
            );
            if (res.ok) {
                dispatch(updateError({
                    message: 'Template updated — pending Meta re-approval.',
                    success: true,
                }));
                setCreateDialogOpen(false);
                setEditingTemplate(null);
                dispatch(getSearchWpTemplateList({ wpAccountId }));
            } else {
                const errMsg = res.data?.detail || res.data?.error || 'Failed to update template';
                dispatch(updateError({ message: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg), success: false }));
            }
        } catch {
            dispatch(updateError({ message: 'Failed to update template', success: false }));
        } finally {
            setSubmitting(false);
        }
    }, [wpAccountId, practiceId]);

    // ── Combined submit router ──
    const handleDialogSubmit = useCallback(async (payload) => {
        if (payload.isEdit) {
            await handleEditTemplate(payload);
        } else {
            await handleCreateTemplate(payload);
        }
    }, [handleCreateTemplate, handleEditTemplate]);

    // ── Open create dialog — prefer external handler if provided ──
    const handleOpenCreate = useCallback(() => {
        if (onRequestTemplate) {
            onRequestTemplate();
        } else {
            setEditingTemplate(null);
            setCreateDialogOpen(true);
        }
    }, [onRequestTemplate]);

    // ── Open edit dialog — prefer external handler if provided ──
    const handleOpenEdit = useCallback(() => {
        if (onEditTemplate) {
            onEditTemplate(selectedTemplate);
        } else if (selectedTemplate) {
            setEditingTemplate(selectedTemplate);
            setCreateDialogOpen(true);
        }
    }, [onEditTemplate, selectedTemplate]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!open || createDialogOpen) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const currentIndex = filteredTemplates.findIndex(t => t.id === selectedTemplate?.id);
                let newIndex;
                if (e.key === 'ArrowDown') {
                    newIndex = currentIndex < filteredTemplates.length - 1 ? currentIndex + 1 : 0;
                } else {
                    newIndex = currentIndex > 0 ? currentIndex - 1 : filteredTemplates.length - 1;
                }
                handleTemplateSelect(filteredTemplates[newIndex]);
            }
            if (e.key === 'Enter' && selectedTemplate) {
                handleSend();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, createDialogOpen, filteredTemplates, selectedTemplate, handleTemplateSelect, handleSend]);

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <m.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                <Box
                    sx={{
                        width: isSmall ? '95vw' : '90vw',
                        maxWidth: 960,
                        maxHeight: isMobile ? '100dvh' : '80vh',
                        height: isMobile ? '100dvh' : (isSmall ? '95vh' : '80vh'),
                        bgcolor: '#fff',
                        borderRadius: '16px',
                        border: '0.5px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto',
                    }}
                >
                    {/* ── Header ── */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.5,
                            py: 1.75,
                            borderBottom: '0.5px solid rgba(0,0,0,0.08)',
                            bgcolor: '#fff',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: WA_LIGHT_GREEN,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    flexShrink: 0,
                                }}
                            >
                                <Icon icon="mdi:whatsapp" width={20} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 650, fontSize: '0.92rem', lineHeight: 1.3, color: 'text.primary', letterSpacing: '-0.01em' }}>
                                    {title}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                    {templates.length} template{templates.length !== 1 ? 's' : ''} available
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Button
                                size="small"
                                onClick={handleOpenCreate}
                                startIcon={<Icon icon="mdi:plus" width={16} />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.76rem',
                                    borderRadius: APPLE.segmentedRadius,
                                    px: 1.5,
                                    py: 0.5,
                                    bgcolor: 'rgba(37,211,102,0.08)',
                                    color: WA_GREEN,
                                    border: '0.5px solid rgba(37,211,102,0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(37,211,102,0.14)',
                                        border: '0.5px solid rgba(37,211,102,0.3)',
                                    },
                                }}
                            >
                                Create Template
                            </Button>
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    width: 30,
                                    height: 30,
                                    color: 'text.secondary',
                                    bgcolor: 'rgba(0,0,0,0.04)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                }}
                            >
                                <Icon icon="mdi:close" width={18} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* ── Content ── */}
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        {/* Left Panel: Search + Filters + List */}
                        <Box
                            sx={{
                                width: isMobile ? '100%' : 320,
                                flexShrink: 0,
                                borderRight: isMobile ? 'none' : '0.5px solid rgba(0,0,0,0.08)',
                                display: isMobile && selectedTemplate && !mobileShowList ? 'none' : 'flex',
                                flexDirection: 'column',
                                bgcolor: '#fff',
                            }}
                        >
                            {/* Search */}
                            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search templates..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon icon="mdi:magnify" width={18} color="rgba(0,0,0,0.3)" />
                                            </InputAdornment>
                                        ),
                                        sx: { fontSize: '0.84rem', height: 38 },
                                    }}
                                    sx={appleInputSx}
                                />
                            </Box>

                            {/* Filter Chips — compact single-line row */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '4px',
                                    px: 2,
                                    pb: 1.5,
                                    overflowX: 'auto',
                                    '&::-webkit-scrollbar': { height: 0 },
                                }}
                            >
                                {usageTypes.map((type) => {
                                    const isActive = selectedCategory === type;
                                    const chipColor = type === 'ALL'
                                        ? { bg: WA_GREEN, color: '#fff' }
                                        : getUsageTypeColor(type, theme);
                                    const label = type === 'ALL'
                                        ? 'All'
                                        : `${formatUsageType(type)} (${usageTypeCounts[type] || 0})`;
                                    return (
                                        <Chip
                                            key={type}
                                            label={label}
                                            onClick={() => setSelectedCategory(type)}
                                            size="small"
                                            sx={{
                                                height: 22,
                                                fontSize: '0.64rem',
                                                fontWeight: 600,
                                                borderRadius: '6px',
                                                flexShrink: 0,
                                                '& .MuiChip-label': { px: 0.75 },
                                                ...(isActive ? {
                                                    bgcolor: type === 'ALL' ? WA_GREEN : chipColor.color,
                                                    color: '#fff',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                                    '&:hover': { bgcolor: type === 'ALL' ? WA_GREEN : chipColor.color },
                                                } : {
                                                    bgcolor: 'rgba(0,0,0,0.04)',
                                                    color: 'text.secondary',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                                }),
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            {/* Template List */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    borderTop: '0.5px solid rgba(0,0,0,0.06)',
                                    '&::-webkit-scrollbar': { width: 4 },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
                                }}
                            >
                                {loading ? (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Loading templates...</Typography>
                                    </Box>
                                ) : filteredTemplates.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Icon icon="mdi:file-search-outline" width={40} color="rgba(0,0,0,0.15)" />
                                        <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 1 }}>
                                            No templates found
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={handleOpenCreate}
                                            startIcon={<Icon icon="mdi:plus" width={14} />}
                                            sx={{
                                                mt: 1.5,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.76rem',
                                                borderRadius: APPLE.segmentedRadius,
                                                color: WA_GREEN,
                                            }}
                                        >
                                            Create a new template
                                        </Button>
                                    </Box>
                                ) : (
                                    <List disablePadding>
                                        {filteredTemplates.map((template) => (
                                            <WhatsAppTemplateListItem
                                                key={template.id}
                                                template={template}
                                                isSelected={selectedTemplate?.id === template.id}
                                                onClick={() => handleTemplateSelect(template)}
                                            />
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Box>

                        {/* Right Panel: Preview */}
                        <Box
                            sx={{
                                flex: 1,
                                display: isMobile && mobileShowList ? 'none' : 'flex',
                                flexDirection: 'column',
                                bgcolor: '#fafafa',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Mobile back button */}
                            {isMobile && selectedTemplate && !mobileShowList && (
                                <Box sx={{ p: 1, borderBottom: '0.5px solid rgba(0,0,0,0.06)', bgcolor: '#fff' }}>
                                    <Button
                                        startIcon={<Icon icon="mdi:arrow-left" />}
                                        onClick={() => { setInternalSelectedTemplate(null); setMobileShowList(true); }}
                                        size="small"
                                        sx={{ fontSize: '0.75rem', textTransform: 'none', color: APPLE.blue, fontWeight: 500 }}
                                    >
                                        Back to list
                                    </Button>
                                </Box>
                            )}

                            {/* Phone mockup preview — scrollable */}
                            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2.5, px: 2 }}>
                                <WhatsAppTemplatePreviewPanel
                                    template={selectedTemplate}
                                    contextMapping={contextMapping}
                                    showVariables
                                />
                            </Box>

                            {/* Footer: Edit + Cancel + Send */}
                            {selectedTemplate && (
                                <Box
                                    sx={{
                                        px: 2.5,
                                        py: 1.5,
                                        borderTop: '0.5px solid rgba(0,0,0,0.08)',
                                        bgcolor: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    {/* Edit button — left side */}
                                    <Tooltip
                                        title="Edit this template. Once edited, it will be sent for Meta approval. Templates can only be edited once every 24 hours."
                                        arrow
                                        placement="top"
                                    >
                                        <Button
                                            size="small"
                                            onClick={handleOpenEdit}
                                            startIcon={<Icon icon="mdi:pencil-outline" width={15} />}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.78rem',
                                                borderRadius: APPLE.inputRadius,
                                                px: 2,
                                                py: 0.65,
                                                color: APPLE.blue,
                                                bgcolor: 'rgba(0,122,255,0.06)',
                                                border: '0.5px solid rgba(0,122,255,0.15)',
                                                '&:hover': {
                                                    bgcolor: 'rgba(0,122,255,0.1)',
                                                    border: '0.5px solid rgba(0,122,255,0.25)',
                                                },
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </Tooltip>

                                    {/* Spacer */}
                                    <Box sx={{ flex: 1 }} />

                                    <Button
                                        onClick={onClose}
                                        size="small"
                                        sx={{
                                            px: 2.5,
                                            py: 0.65,
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            borderRadius: APPLE.inputRadius,
                                            textTransform: 'none',
                                            bgcolor: 'rgba(0,0,0,0.04)',
                                            color: 'text.secondary',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleSend}
                                        startIcon={<Icon icon="mdi:send" width={15} />}
                                        size="small"
                                        sx={{
                                            px: 2.5,
                                            py: 0.65,
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            borderRadius: APPLE.inputRadius,
                                            textTransform: 'none',
                                            bgcolor: WA_LIGHT_GREEN,
                                            color: '#fff',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 0 16px rgba(37,211,102,0.2)',
                                            '&:hover': { bgcolor: WA_GREEN, boxShadow: '0 2px 8px rgba(0,0,0,0.16), 0 0 20px rgba(37,211,102,0.25)' },
                                        }}
                                    >
                                        Send Template
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
                </m.div>
            </Modal>

            {/* ── Built-in Create / Edit Dialog ── */}
            <RequestTemplateDialog
                open={createDialogOpen}
                onClose={() => {
                    setCreateDialogOpen(false);
                    setEditingTemplate(null);
                }}
                onSubmit={handleDialogSubmit}
                submitting={submitting}
                editTemplate={editingTemplate}
                existingTemplateNames={existingNames}
            />
        </>
    );
};

WhatsAppTemplateSelector.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    templates: PropTypes.array,
    onSend: PropTypes.func,
    onSelect: PropTypes.func,
    selectedTemplate: PropTypes.object,
    loading: PropTypes.bool,
    contextMapping: PropTypes.object,
    title: PropTypes.string,
    onRequestTemplate: PropTypes.func,
    onEditTemplate: PropTypes.func,
};

WhatsAppTemplateSelector.defaultProps = {
    templates: [],
    onSend: undefined,
    onSelect: undefined,
    selectedTemplate: null,
    loading: false,
    contextMapping: {},
    title: 'Select WhatsApp Template',
    onRequestTemplate: undefined,
    onEditTemplate: undefined,
};

export default WhatsAppTemplateSelector;
